import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { actions as notificationActions } from '@bufferapp/notifications';
import { actions as composerPopoverActions } from '~/composer-popover';
import { useShowThreadsUpgrade } from '~/utils/canQueueMoreThreadsForAllTwitterProfiles';
import { PublishingPost } from '~/post/types.generated';
import { usePostActions, usePostPermissions } from '~/post/hooks';
import { PostEntity } from '~/post/PostEntity';
import PostFooterButtons from '~/post/components/PostFooterButtons';
import { sharePostNow } from '~/post/thunks/sharePostNow';
import {
  approveDraft,
  changeDraftStatus,
  movePostToDrafts,
} from '~/post/thunks';
import { useDeletePost } from './hooks';

export const EditDeleteActions = ({
  post,
  setOpenModal,
}: {
  post: PublishingPost;
  setOpenModal: () => void;
}): JSX.Element => {
  const [isWorking, setIsWorking] = useState(false);
  const dispatch = useDispatch();
  // TODO: This is a temporary solution until we improve our Pusher handling
  // Instead of setting isWorking to false on dispatch complete
  // we do it if the hook reloads with an updated post
  // because we force refetches on pusher events
  useEffect(() => setIsWorking(false), [post]);

  const postId = post?.id;
  const isDraft = PostEntity.isDraft(post);
  const { hasEditPermission } = usePostPermissions(post);

  const onRescheduleClick = (): void => {
    dispatch(composerPopoverActions.handleEditClick(postId));
  };

  const onApproveClick = async (): Promise<void> => {
    setIsWorking(true);
    await dispatch(approveDraft({ postId }));
  };

  const onRequestApprovalClick = async (): Promise<void> => {
    setIsWorking(true);
    await dispatch(changeDraftStatus({ postId, needsApproval: true }));
  };

  const onMovePostToDraftsClick = async (): Promise<void> => {
    setIsWorking(true);
    await dispatch(movePostToDrafts({ postId }));
  };

  const onRevertApprovalClick = async (): Promise<void> => {
    setIsWorking(true);
    await dispatch(changeDraftStatus({ postId, needsApproval: false }));
  };

  const onSharePostNowClick = async (): Promise<void> => {
    setIsWorking(true);
    await dispatch(sharePostNow({ postId: post.id }));
  };

  const { singleActions, stackedActions } = usePostActions({
    post,
    onRequestApprovalClick,
    onRescheduleClick,
    onApproveClick,
    onMovePostToDraftsClick,
    onRevertApprovalClick,
    onSharePostNowClick,
  });

  const onEditClick = (): void => {
    dispatch(composerPopoverActions.handleEditClick(postId));
  };

  const onDeleteSuccess = (): void => {
    dispatch(
      notificationActions.createNotification({
        notificationType: 'success',
        message: `Okay, we've deleted that ${isDraft ? 'draft' : 'post'}!`,
      })
    );
  };

  const onDeleteError = (error): void => {
    const message =
      error ||
      `Whoops, we had some problems deleting that ${
        isDraft ? 'draft' : 'post'
      }!`;
    dispatch(
      notificationActions.createNotification({
        notificationType: 'error',
        message,
      })
    );
  };

  const { deletePost, processing } = useDeletePost({
    postId,
    onDeleteSuccess,
    onDeleteError,
  });

  const isLoading = processing || isWorking;

  const { shouldShowThreadsMigrationModal: overLimit } =
    useShowThreadsUpgrade(post);

  // for the calendar, we can't only rely on the regular flag,
  // we check stackedActions has MOVE_POST_TO_DRAFTS to check if it's the 1 thread they are allowed
  // we check singleActions has 'SCHEDULE_POST' to check if it's a draft thread
  const showUpgradeButton =
    overLimit &&
    ((stackedActions && stackedActions[1]?.id === 'MOVE_POST_TO_DRAFTS') ||
      (singleActions && singleActions[0]?.id === 'SCHEDULE_POST'));

  return (
    <PostFooterButtons
      isLoading={isLoading}
      singleActions={singleActions}
      stackedActions={stackedActions}
      onDeleteClick={hasEditPermission ? deletePost : undefined}
      onEditClick={hasEditPermission ? onEditClick : undefined}
      shouldShowThreadsMigrationModal={showUpgradeButton}
      setOpenModal={setOpenModal}
    />
  );
};

EditDeleteActions.defaultProps = {};
